import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiV1, setAccessToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setUser } = useAuth();
  const [err, setErr] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(loc.search);
    const code = params.get('code');
    const state = params.get('state');
    const errorParam = params.get('error');
    const errorDescription = params.get('error_description');
    if (errorParam) {
      setErr(`${errorParam}: ${errorDescription || 'unknown error'}`);
      return;
    }
    if (!code || !state) {
      setErr('Missing code/state in callback');
      return;
    }
    (async () => {
      try {
        const redirectUri = `${window.location.origin}/auth/callback`;
        const r = await apiV1.post('/auth/login/callback', { code, state, redirect_uri: redirectUri });
        setAccessToken(r.data.access_token);
        setUser?.(r.data.user);
        const next = sessionStorage.getItem('hireiq:postLoginNext') || '/portal';
        sessionStorage.removeItem('hireiq:postLoginNext');
        nav(next, { replace: true });
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [loc.search, nav, setUser]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm" data-testid="auth-callback">
        {!err ? (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-sm font-medium">Signing you in…</p>
            <p className="text-xs text-muted-foreground">Validating Microsoft identity and provisioning your HireIQ session.</p>
          </>
        ) : (
          <>
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-rose-50 text-rose-600"><AlertTriangle className="h-6 w-6" /></div>
            <p className="mt-4 text-sm font-semibold">Sign-in failed</p>
            <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-secondary/50 p-3 text-left text-[11px] text-muted-foreground whitespace-pre-wrap">{typeof err === 'string' ? err : JSON.stringify(err, null, 2)}</pre>
            <Button className="mt-4 w-full" onClick={() => nav('/login', { replace: true })}>Back to sign in</Button>
          </>
        )}
      </div>
    </div>
  );
}
